import React from "react";
import { Box, Button, Divider, Grid, IconButton, InputAdornment, TextareaAutosize, TextField, Typography } from "@mui/material";

import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import DataObjectIcon from '@mui/icons-material/DataObject';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import Delete from '@mui/icons-material/Delete';
import { GridCloseIcon } from "@mui/x-data-grid";

import globalStyles from "../../../../../../assets/styles/global.module.scss";
import styles from "../../../../../../assets/styles/componentStyles/templateLibraryStyles/modifyWhatsAppTemplate/whatsAppTemplateForm.module.scss";

import Required from "../../../../../utility/required";
import { useTemplateLibraryStore } from "../../../../../../store/templateLibraryStore";
import { useShallow } from "zustand/react/shallow";
import { handleKeyDownForFormatting, hasConsecutiveNewlinesInCarouselBody, isTemplateCreated } from "../../../constant";
import { useCarouselFunctions } from "../../../utils/useCarouselFunctions";
import { CAROUSEL_MAX_CHARS, displayFlexCursorPointerClassName, renderTooManyVariableParameterWarning, VARIABLE_REGEX } from "../../../../channels/global/whatsapp/templates/utils";
import { whatsappCarouselCardData } from "../../../../../../models/axiosModel";
import { useInsertVariableAtCursorPosition } from "../../../../../../hooks/useInsertVariableAtCursorPosition";
import EmojiPickerButton from "../../../utils/EmojiPickerButton";
import insertTextAtCursor from "../../../utils/insertTextAtCursor";
import TouchTooltip from "../../../utils/TouchTooltip";
import VariableDisclaimer from "../../../utils/VariableDisclaimer";
import SampleVariableKeyLabel from "../../../utils/SampleVariableKeyLabel";

const CarouselFormCardBody = (props: any) => {

    const { cardData, cardIndex, handleChangeText } = props;

    const { textareaRefs, handleFormattingCarousel, handleAddCarouselField, checkDisableAddVarButtonCarousel, checkFlagErrorCarousel, 
        handleChangeCarouselVar, handleRemoveCarouselField, updateCarouselCardBodyText 
    } = useCarouselFunctions();

    const { insertVariable, registerTextarea } = useInsertVariableAtCursorPosition();

    const { createTemplatePayload } = useTemplateLibraryStore(useShallow((state) => ({
        createTemplatePayload: state.createTemplatePayload,
    })));

    const cardBodyLength = (cardData: whatsappCarouselCardData) => {
        return cardData?.whatsapp_template_body?.whatsapp_template_body_value?.replace(VARIABLE_REGEX['FLAG_NUMBER'], '*')?.length ?? 0;
    }

    const handleCarouselBodyTextChange = (value: string, cursorPosition?: number) => {
        updateCarouselCardBodyText(value, cardIndex, handleChangeText, cursorPosition);
    };

    const handleInsertEmoji = (emoji: string, selection: { start: number; end: number } | null) => {
        const latestPayload = useTemplateLibraryStore.getState().createTemplatePayload;
        const bodyValue = latestPayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card?.[cardIndex]?.whatsapp_template_body?.whatsapp_template_body_value ?? "";
        const start = selection?.start ?? bodyValue.length;
        const end = selection?.end ?? bodyValue.length;
        const result = insertTextAtCursor(bodyValue, emoji, start, end);

        handleCarouselBodyTextChange(result.value, result.cursorPosition);
    };

    const isTemplateFormattingDisabled = isTemplateCreated(createTemplatePayload);
    const formattingIconClassName = `${displayFlexCursorPointerClassName} ${styles.formattingIcon} ${isTemplateFormattingDisabled ? styles.disabledIcon : ''}`;

    const bodyTextValue = cardData?.whatsapp_template_body?.whatsapp_template_body_value;
    const showConsecutiveNewlinesError = hasConsecutiveNewlinesInCarouselBody(bodyTextValue);

    return (
        <Grid container className={styles.container}>

            <Grid item xs={12}>
                <Grid container justifyContent={"space-between"} alignItems={"center"} flexWrap={"nowrap"}>
                    <Typography component={'span'} className={styles.templateHeadtext}>
                        Template Body
                        <Required />
                    </Typography>

                    <span className={styles.accordion__summary__subTextValue}>
                        {`${cardBodyLength(cardData)}/${CAROUSEL_MAX_CHARS?.BODY_TEXT}`}
                    </span>
                </Grid>
            </Grid>

            <Grid item xs={12} className={`${styles.whatsappTemplateFormBody__bodyTextContainer} ${globalStyles.marginTop5px}`}>

                {/* Formatting */}
                <Grid container className={styles.formattingContainer}>

                    {/* Formatting Buttons */}
                    <Grid item sx={{ width: "auto", flexShrink: 0 }}>
                        <Box className={styles.formattingToolbarGroup}>
                            
                            <Box className={styles.formattingToolbarIcons}>
                                <TouchTooltip title={"Bold (Ctrl + B)"}>
                                    <FormatBoldIcon
                                        className={formattingIconClassName}
                                        onClick={() => handleFormattingCarousel('bold', 'body', cardIndex)}
                                    />
                                </TouchTooltip>

                                <TouchTooltip title={"Italic (Ctrl + I)"}>
                                    <FormatItalicIcon
                                        className={formattingIconClassName}
                                        onClick={() => handleFormattingCarousel('italic', 'body', cardIndex)}
                                    />
                                </TouchTooltip>

                                <TouchTooltip title={"Strikethrough (Ctrl + S)"}>
                                    <StrikethroughSIcon
                                        className={formattingIconClassName}
                                        onClick={() => handleFormattingCarousel('strikethrough', 'body', cardIndex)}
                                    />
                                </TouchTooltip>
                            </Box>

                            <Divider orientation="vertical" flexItem className={styles.formattingToolbarDivider} />

                            <EmojiPickerButton
                                getTextareaElement={() => textareaRefs.current[cardIndex] ?? null}
                                disabled={isTemplateCreated(createTemplatePayload)}
                                className={styles.formattingIcon}
                                onSelectEmoji={handleInsertEmoji}
                            />
                        </Box>
                    </Grid>

                    {/* Add Variable Button */}
                    <Grid item className={styles.addVariableButtonContainer} sx={{ width: "auto" }}>
                        <Button
                            variant="outlined"
                            size='small'
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleAddCarouselField("body", insertVariable, cardIndex)}
                            className={`${styles.callToActionButton} ${globalStyles.clickable}`}
                            disabled={isTemplateCreated(createTemplatePayload)}
                            sx={{
                                ...(checkDisableAddVarButtonCarousel() && {
                                    opacity: "0.5"
                                }),
                                visibility: isTemplateCreated(createTemplatePayload) ? "hidden" : "visible",
                            }}
                            startIcon={<DataObjectIcon />}
                        >
                            Add Variable
                        </Button>
                    </Grid>
                    
                </Grid>

                {/* Body Textarea */}
                <TextareaAutosize
                    disabled={isTemplateCreated(createTemplatePayload)}
                    ref={(el: HTMLTextAreaElement | null) => {
                        textareaRefs.current[cardIndex] = el
                        registerTextarea(`body${cardIndex}`, el);
                    }}
                    className={styles.whatsappTemplateFormBody__bodyTextArea}
                    value={bodyTextValue}
                    onChange={(event: any) => handleCarouselBodyTextChange(event.target.value)}
                    onKeyDown={(e) => handleKeyDownForFormatting(e, handleFormattingCarousel, "body", cardIndex)}
                    autoComplete='off'
                    placeholder='Enter template body'
                    dir="auto"
                />
            </Grid>

            {/* Too Many Variable Warning */}
            {checkFlagErrorCarousel("body", cardIndex) &&
                renderTooManyVariableParameterWarning()
            }

            {/* Body Text Sample Variables */}
            <Grid item xs={12} className={globalStyles.marginTop5px}>
                {cardData?.whatsapp_template_body?.whatsapp_template_body_variable_data?.map((field: any, ind: number) => (
                    <React.Fragment key={ind}>
                        <Grid item xs={12} sx={{ margin: '10px 0px' }}>
                            <Grid container sx={{ alignItems: 'center' }} flexWrap={"nowrap"}>
                                
                                <Grid item className={styles.sampleVariableKeyColumn}>
                                    <SampleVariableKeyLabel
                                        variableKey={`${field?.whatsapp_template_dynamic_variable_key}`}
                                        className={styles.sampleVariableKeyText}
                                    />
                                </Grid>

                                <Grid item xs={9} key={field?.whatsapp_template_dynamic_variable_id}>
                                    <TextField
                                        disabled={isTemplateCreated(createTemplatePayload)}
                                        size='small'
                                        variant="outlined"
                                        className={styles.sampleVariableTextField}
                                        // value={field.value}
                                        onChange={(e) => handleChangeCarouselVar(field?.whatsapp_template_dynamic_variable_key, e, "body", cardIndex)}
                                        fullWidth
                                        autoComplete='off'
                                        value={field?.whatsapp_template_dynamic_variable_sample_value}
                                        sx={{
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                border: 'none'
                                            }
                                        }}
                                        inputProps={{ dir: 'auto' }}
                                        InputProps={{
                                            endAdornment: (!isTemplateCreated(createTemplatePayload) && field?.whatsapp_template_dynamic_variable_sample_value?.trim() !== '') ? (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        size="small"
                                                        edge="end"
                                                        aria-label="Clear sample text"
                                                        onClick={() => handleChangeCarouselVar(field?.whatsapp_template_dynamic_variable_key, { target: { value: '' } }, "body", cardIndex)}
                                                        className={globalStyles.clickable}
                                                    >
                                                        <GridCloseIcon className={styles.sampleVariableClearIcon} />
                                                    </IconButton>
                                                </InputAdornment>
                                            ) : null,
                                        }}
                                    />
                                </Grid>

                                {!isTemplateCreated(createTemplatePayload) && (
                                    <Grid item sx={{ marginLeft: '5px' }}>
                                        <IconButton
                                            onClick={() => handleRemoveCarouselField(field?.whatsapp_template_dynamic_variable_key, "body", cardIndex)}
                                            disabled={isTemplateCreated(createTemplatePayload)}
                                            className={styles.sampleVariableDeleteButton}
                                        >
                                            <Delete className={styles.sampleVariableDeleteIcon} />
                                        </IconButton>
                                    </Grid>
                                )}
                                
                            </Grid>
                        </Grid>
                    </React.Fragment>
                ))}
            </Grid>

            {/* Add Sample Text */}
            {(cardData?.whatsapp_template_body?.whatsapp_template_body_value?.match(VARIABLE_REGEX['FLAG_NUMBER'])?.length ?? 0) > 0 &&
                (cardData?.whatsapp_template_body?.whatsapp_template_body_variable_data ?? []).filter((data: any) => (data?.whatsapp_template_dynamic_variable_sample_value ?? "").trim() === "").length !== 0 &&
                <Grid item marginBlock={"12px"} xs={12}>
                    <VariableDisclaimer text="Add sample text" />
                </Grid>
            }

            {/* Consecutive Newlines Error */}
            {showConsecutiveNewlinesError && (
                <Grid item xs={12} className={globalStyles.marginTop10px}>
                    <VariableDisclaimer text="Paragraphs are not supported in the body." />
                </Grid>
            )}

        </Grid>
    )
}

export default CarouselFormCardBody;